import { roundDisplayValueTo, type Nil } from '@core';
import { GraphLineNode } from '@data';
import { useTranslate } from '@i18n';

import { graphPalette } from '../../../colors';
import { u } from '../../../styles';
import { Box } from '../../Layout';
import { MenuItem } from '../../Menu/Menu';
import { ParameterCard } from '../../ParameterCard';
import { SelectBox } from '../../SelectBox/SelectBox';
import { type MeasurementValue } from '../Graph';

import * as css from './LegendAside.css';

import type { GraphColorVariants } from '../../SelectBox/SelectBox';

const getSelectColor = (color: string | undefined): GraphColorVariants => {
    const colorEntry = Object.entries(graphPalette).find(([, graphColor]) => graphColor === color);
    return (colorEntry?.[0] as GraphColorVariants | undefined) ?? 'blue';
};

export type LegendAsideProps = {
    data: Record<string, MeasurementValue | Nil> | Nil;
    colorMap: Record<string, string>;
    lines: GraphLineNode[] | undefined;
    filteredDataKeys: string[];
    showComparison: boolean;
    showComparisonData: boolean;
    onClickLegend: (value: string) => void;
    guideParameters?: Record<string, number>;
    onClickGuide?: (smpId: number) => void;
};

export function Legend({
    data,
    lines,
    colorMap,
    filteredDataKeys,
    showComparison,
    showComparisonData,
    onClickLegend,
    guideParameters,
    onClickGuide,
}: LegendAsideProps) {
    const keys = Object.keys(data ?? {}).sort((a, b) => a.localeCompare(b));
    const MAX_LEGENDS = 5;
    const t = useTranslate();

    return (
        <Box gap={8} pt={u(3)} className={css.aside}>
            {keys.slice(0, MAX_LEGENDS).map((key) => {
                const line = lines?.find((l) => l.id === key);
                if (!line) return null;

                const measurement = data?.[key];
                if (!measurement) return null;

                const { current, comparison, displaySymbol, displayUnit } = measurement;
                const color = colorMap[line.id];

                const isFilteredOut = filteredDataKeys.includes(key);
                const comparisonValue =
                    showComparison && showComparisonData && typeof comparison === 'number' ? comparison : undefined;
                const guideParameterId = guideParameters?.[line.timeSeries.id];

                const endSlot =
                    guideParameterId && onClickGuide ? (
                        <ParameterCard.Menu>
                            <MenuItem
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClickGuide(guideParameterId);
                                }}
                            >
                                {t('swing_guide.title')}
                            </MenuItem>
                        </ParameterCard.Menu>
                    ) : undefined;

                return (
                    <ParameterCard.Root key={key} small compact onClick={() => onClickLegend(key)}>
                        <ParameterCard.Title
                            startSlot={
                                <SelectBox color="gray" selected={!isFilteredOut} selectColor={getSelectColor(color)} />
                            }
                            endSlot={endSlot}
                            compact
                        >
                            {t(line.i18n ?? '-')}
                        </ParameterCard.Title>
                        <ParameterCard.Values>
                            <ParameterCard.Value
                                displayUnit={displayUnit}
                                value={roundDisplayValueTo({ value: current, displayUnit })}
                                symbol={displaySymbol}
                            />
                            <ParameterCard.Comparison
                                displayUnit={displayUnit}
                                value={roundDisplayValueTo({ value: comparisonValue, displayUnit })}
                                symbol={displaySymbol}
                                showSymbol
                            />
                        </ParameterCard.Values>
                    </ParameterCard.Root>
                );
            })}
        </Box>
    );
}
